import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useSelector, useDispatch } from 'react-redux';
import IcomoonReact from 'icomoon-react';

import LeaderboardTemplateCard from './LeaderboardTemplateCard';

import Button from '../../../shared/_TBComponents/Button';
import { showErrorToast } from '../../../shared/utils/toastManager';

import { fetchLeaderboard, setModalOpen } from '../ducks/leaderboardActions';
import { filterAndSortByGroups } from '../helpers';

import iconSet from '../../../shared/images/teambuildr-selection.json';

const GridOuter = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow-y: auto;
`;

const AddCardPadder = styled('div')`
  padding: 10px;
`;

const AddCard = styled('div')`
  width: 300px;
  height: 250px;
  background: #ffffff;
  padding: 20px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 12px;
  border-radius: 8px;
  font-family: 'Nunito Sans';
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);

  &:hover {
    box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.15);
  }
`;

const AddCardTopSpacer = styled('div')`
  height: 19.5px;
  flex-shrink: 0;
`;

const PlusIconContainer = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  width: 52px;
  height: 52px;
  background: rgba(23, 135, 251, 0.6);
`;

const AddCardLabel = styled('div')`
  font-size: 15px;
  font-weight: 600;
  line-height: 1.3;
  text-align: center;
  color: #333;
`;

const GridContainer = styled('div')`
  display: flex;
  flex-wrap: wrap;
  padding: 10px 20px;
`;

const ZeroStateWrapper = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 40px 20px;
`;

const ZeroStateCard = styled('div')`
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.08);
  padding: 48px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 560px;
  width: 100%;
  text-align: center;
`;

const PodiumIllustration = styled('div')`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  margin-bottom: 28px;
`;

const PodiumColumn = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const MedalCircle = styled('div')`
  width: 52px;
  height: 52px;
  background: white;
  border-radius: 50%;
  border: 2px solid ${({ borderColor }) => borderColor};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PodiumBar = styled('div')`
  width: 90px;
  height: ${({ height }) => height}px;
  background: ${({ bgColor }) => bgColor};
  border-radius: 6px 6px 0 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 12px;
  color: white;
  font-family: 'Nunito Sans';
  font-size: 20px;
  font-weight: 700;
`;

const ZeroStateHeading = styled('p')`
  font-family: 'Nunito Sans';
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 12px;
  line-height: 1.4;
`;

const ZeroStateSubtext = styled('p')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: #6b7280;
  max-width: 400px;
  margin: 0 0 32px;
  line-height: 1.6;
`;

const LeaderboardTemplatesGrid = ({ searchTerm }) => {
  const templates = useSelector((state) => state.leaderboard.data.templates);
  const activeFilters = useSelector((state) => state.leaderboard.ui.activeFilters);
  const accountCode = useSelector((state) => state.auth.data.currentUser.accountCode);
  const dispatch = useDispatch();

  const { exerciseId, groups: filterGroups } = activeFilters;
  const hasActiveFilters = exerciseId || filterGroups.length > 0 || searchTerm.length > 0;

  const bySearch = searchTerm.length > 0
    ? templates.filter((t) => t.exerciseName.toLowerCase().includes(searchTerm.toLowerCase()))
    : templates;

  const byExercise = exerciseId
    ? bySearch.filter((t) => t.exerciseId === exerciseId)
    : bySearch;

  const filtered = filterGroups.length > 0
    ? filterAndSortByGroups(byExercise, filterGroups)
    : byExercise;

  const showNoResults = hasActiveFilters && filtered.length === 0;
  const showZeroState = templates.length === 0;
  const showEmptyState = showZeroState || showNoResults;

  const emptyHeading = showZeroState ? 'Rank your athletes' : 'No results found';
  const emptySubtext = showZeroState
    ? 'Build a leaderboard for any lift, SAQ+C event, or circuit — then save your favorites so they\'re always one click away.'
    : 'No saved leaderboards match your current filters. Try adjusting your search or create a new leaderboard.';

  const handleAddNew = () => {
    dispatch(setModalOpen(true, 'Create Leaderboard'));
  };

  const handleLoadTemplate = async (template) => {
    const { id, ...formValues } = template;
    try {
      await dispatch(fetchLeaderboard(
        { ...formValues, accountCode },
        'add',
        undefined,
        true,
        id,
      ));
    } catch (err) {
      showErrorToast(err?.response?.headers?.message || 'Failed to load leaderboard. Please try again.');
    }
  };

  const createCard = (
    <AddCardPadder onClick={handleAddNew}>
      <AddCard>
        <AddCardTopSpacer />
        <PlusIconContainer>
          <IcomoonReact iconSet={iconSet} size={25} icon='plus' color='white' />
        </PlusIconContainer>
        <AddCardLabel>Create New Leaderboard</AddCardLabel>
      </AddCard>
    </AddCardPadder>
  );

  return (
    <GridOuter>
      {showEmptyState && (
        <ZeroStateWrapper>
          <ZeroStateCard>
            <PodiumIllustration>
              <PodiumColumn>
                <MedalCircle borderColor='#979797'>
                  <IcomoonReact iconSet={iconSet} size={22} icon='ribbon' color='#979797' />
                </MedalCircle>
                <PodiumBar height={80} bgColor='rgba(23, 135, 251, 0.6)'>2</PodiumBar>
              </PodiumColumn>
              <PodiumColumn>
                <MedalCircle borderColor='#f9b242'>
                  <IcomoonReact iconSet={iconSet} size={22} icon='trophy' color='#f9b242' />
                </MedalCircle>
                <PodiumBar height={110} bgColor='rgba(23, 135, 251, 1)'>1</PodiumBar>
              </PodiumColumn>
              <PodiumColumn>
                <MedalCircle borderColor='#d0740d'>
                  <IcomoonReact iconSet={iconSet} size={22} icon='ribbon' color='#d0740d' />
                </MedalCircle>
                <PodiumBar height={60} bgColor='rgba(23, 135, 251, 0.35)'>3</PodiumBar>
              </PodiumColumn>
            </PodiumIllustration>
            <ZeroStateHeading>{emptyHeading}</ZeroStateHeading>
            <ZeroStateSubtext>{emptySubtext}</ZeroStateSubtext>
            <Button
              name='+ New Board'
              bgColor='#444444'
              txtColor='white'
              noBorder
              customBorderRadius='8px'
              styledTextProps={{ fontWeight: 700, fontSize: 14 }}
              styledWrapperProps={{ height: '40px', padding: '0 20px' }}
              onClick={handleAddNew}
            />
          </ZeroStateCard>
        </ZeroStateWrapper>
      )}
      {!showEmptyState && (
        <GridContainer>
          {createCard}
          {filtered.map((template) => (
            <LeaderboardTemplateCard
              key={template.id}
              template={template}
              onLoad={handleLoadTemplate}
            />
          ))}
        </GridContainer>
      )}
    </GridOuter>
  );
};

LeaderboardTemplatesGrid.defaultProps = {
  searchTerm: '',
};

LeaderboardTemplatesGrid.propTypes = {
  searchTerm: PropTypes.string,
};

export default LeaderboardTemplatesGrid;
